import { useEffect, useRef, useState, useMemo } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";

const EMA_COLORS = {
  ema9: "#38BDF8",
  ema20: "#FACC15",
  ema50: "#FB923C",
  ema200: "#E879F9",
  vwap: "#A855F7",
};

/**
 * Candlestick chart with volume histogram + toggleable overlays + optional
 * price lines (entry/stop/targets).
 *
 * Props:
 *   bars: [{t, o, h, l, c, v}] (ms epoch)
 *   overlays: { ema9: [{time,value}], ema20, ema50, ema200, vwap }
 *   overlaysVisible: { ema9: bool, ... }
 *   priceLines: [{price, color, title, lineStyle?}]
 */
export default function CandlestickChart({ bars, overlays, overlaysVisible, priceLines = [], height = 500 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleRef = useRef(null);
  const volumeRef = useRef(null);
  const overlayRefs = useRef({}); // {key: series}
  const priceLineRefs = useRef([]); // [priceLine]

  // Initialize chart once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      layout: {
        background: { color: "#0F1626" },
        textColor: "#94A3B8",
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(30, 41, 59, 0.4)" },
        horzLines: { color: "rgba(30, 41, 59, 0.4)" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "#334155", width: 1, style: 3, labelBackgroundColor: "#0F1626" },
        horzLine: { color: "#334155", width: 1, style: 3, labelBackgroundColor: "#0F1626" },
      },
      rightPriceScale: {
        borderColor: "#1E293B",
        scaleMargins: { top: 0.05, bottom: 0.28 },
      },
      timeScale: {
        borderColor: "#1E293B",
        timeVisible: true,
        secondsVisible: false,
      },
      localization: {
        locale: "en-US",
      },
    });

    const candle = chart.addCandlestickSeries({
      upColor: "#10B981",
      downColor: "#EF4444",
      borderUpColor: "#10B981",
      borderDownColor: "#EF4444",
      wickUpColor: "#10B981",
      wickDownColor: "#EF4444",
    });

    const volume = chart.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "volume_scale",
      color: "#334155",
    });
    chart.priceScale("volume_scale").applyOptions({
      scaleMargins: { top: 0.78, bottom: 0 },
      borderColor: "#1E293B",
    });

    chartRef.current = chart;
    candleRef.current = candle;
    volumeRef.current = volume;

    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
        });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      volumeRef.current = null;
      overlayRefs.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  // Update bars
  useEffect(() => {
    if (!candleRef.current || !volumeRef.current) return;
    const candleData = (bars || []).map((b) => ({
      time: Math.floor(b.t / 1000),
      open: b.o,
      high: b.h,
      low: b.l,
      close: b.c,
    }));
    const volData = (bars || []).map((b) => ({
      time: Math.floor(b.t / 1000),
      value: b.v || 0,
      color: b.c >= b.o ? "rgba(16,185,129,0.55)" : "rgba(239,68,68,0.55)",
    }));
    candleRef.current.setData(candleData);
    volumeRef.current.setData(volData);
    if (candleData.length && chartRef.current) {
      chartRef.current.timeScale().fitContent();
    }
  }, [bars]);

  // Update overlays
  useEffect(() => {
    if (!chartRef.current) return;
    const chart = chartRef.current;
    const known = ["ema9", "ema20", "ema50", "ema200", "vwap"];
    known.forEach((key) => {
      const visible = overlaysVisible?.[key];
      const data = overlays?.[key];
      const existing = overlayRefs.current[key];
      if (visible && data && data.length) {
        if (!existing) {
          const s = chart.addLineSeries({
            color: EMA_COLORS[key],
            lineWidth: key === "vwap" ? 2 : 1.5,
            lineStyle: key === "vwap" ? 2 : 0,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          s.setData(data);
          overlayRefs.current[key] = s;
        } else {
          existing.setData(data);
        }
      } else if (existing) {
        chart.removeSeries(existing);
        delete overlayRefs.current[key];
      }
    });
  }, [overlays, overlaysVisible]);

  // Update price lines (entry, stop, targets)
  useEffect(() => {
    if (!candleRef.current) return;
    // Remove old
    priceLineRefs.current.forEach((pl) => {
      try { candleRef.current.removePriceLine(pl); } catch {}
    });
    priceLineRefs.current = [];
    (priceLines || []).forEach((pl) => {
      if (pl.price == null) return;
      const line = candleRef.current.createPriceLine({
        price: pl.price,
        color: pl.color,
        lineWidth: 1,
        lineStyle: pl.lineStyle ?? 2, // dashed
        axisLabelVisible: true,
        title: pl.title,
      });
      priceLineRefs.current.push(line);
    });
    // Expand price scale so all lines are visible (extend by up to 4% margin
    // beyond the min/max of bars+price lines).
    if (chartRef.current && priceLines && priceLines.length) {
      // lightweight-charts autoscale can be extended via priceScale options
      try {
        chartRef.current.priceScale("right").applyOptions({
          scaleMargins: { top: 0.08, bottom: 0.28 },
          autoScale: true,
        });
      } catch {}
    }
  }, [priceLines]);

  return (
    <div
      ref={containerRef}
      data-testid="candlestick-chart-container"
      className="w-full"
      style={{ height }}
    />
  );
}

export { EMA_COLORS };
